import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { events, track } from '@/lib/analytics';
import { getMembershipSummary, type MembershipSummary, type Program } from '@/lib/api';

const PROGRAM_LABEL: Record<Program, string> = {
  Boxing: 'Boxing',
  MuayThai: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu-Jitsu',
  Kids: 'Kids',
};

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

export function JoinSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? '';
  const [summary, setSummary] = useState<MembershipSummary | null>(null);
  const [summaryState, setSummaryState] = useState<'loading' | 'ok' | 'unavailable'>('loading');

  useEffect(() => {
    track(events.joinSuccess);
    track(events.membershipSignupConfirmation, {
      funnel: 'membership_signup',
      step: 'confirmation',
    });
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setSummaryState('unavailable');
      return;
    }
    let cancelled = false;
    // One retry: the payment already succeeded, so a transient blip here must not
    // silently cost the member their program-filtered schedule link.
    async function fetchSummary() {
      const first = await getMembershipSummary(sessionId);
      const result = first ?? (await getMembershipSummary(sessionId));
      if (cancelled) return;
      setSummary(result);
      setSummaryState(result ? 'ok' : 'unavailable');
    }
    void fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <Section eyebrow="Welcome to the team" title="Membership active.">
      <p className="max-w-2xl text-bone/80">
        Your membership is now active. We just sent your receipt and welcome details to your inbox.
      </p>

      {summary && (
        <dl
          className="mt-6 grid max-w-md gap-3 rounded-lg border border-bone/15 bg-neutral-900/60 p-5 text-sm"
          data-testid="membership-summary"
        >
          <div className="flex justify-between gap-4">
            <dt className="text-bone/60">Programs</dt>
            <dd className="text-right font-medium text-bone/90">
              {summary.programs.length > 0
                ? summary.programs.map((p) => PROGRAM_LABEL[p as Program] ?? p).join(', ')
                : '—'}
              {summary.athletes ? ' · athlete pricing' : ''}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-bone/60">Monthly payment</dt>
            <dd className="text-right font-medium text-bone/90">
              {dollars(summary.monthlyAmountCents)} / month
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-bone/60">Commitment</dt>
            <dd className="text-right font-medium text-bone/90" data-testid="summary-commitment">
              {summary.commitmentMonths
                ? `${summary.commitmentMonths}-Month Program · billed monthly`
                : 'Month-to-month · cancel with 30-day notice'}
            </dd>
          </div>
        </dl>
      )}

      {summaryState === 'unavailable' && (
        <p className="mt-6 max-w-md text-sm text-bone/60" data-testid="summary-unavailable">
          We could not load your plan details just now. Your membership is active and your receipt
          is on its way by email. The link below shows the full schedule.
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to={
            summary && summary.programs.length > 0
              ? `/schedule?programs=${encodeURIComponent(summary.programs.join(','))}`
              : '/schedule'
          }
          className="btn-primary inline-flex"
        >
          {summary && summary.programs.length > 0
            ? 'See the class schedule'
            : 'See the full schedule'}
        </Link>

        {summary?.subscriptionId && (
          <Link
            to={`/account?subscription_id=${encodeURIComponent(summary.subscriptionId)}`}
            className="btn-ghost inline-flex"
            data-testid="manage-subscription"
          >
            Manage your subscription
          </Link>
        )}
      </div>

    </Section>
  );
}
